import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, BarChart3, Copy, Download, MoreVertical, Pencil, RefreshCw, Trash2, TriangleAlert } from 'lucide-react'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, LabelList, Pie, PieChart, XAxis, YAxis } from 'recharts'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart'
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { StatusBadge } from '../components/badges'
import type { SubmissionStatus } from '../types'
import {
    formatValue, pieRollup, pointLabel, summarize,
    type ResultRow, type Widget, type WidgetResult,
} from './model'
import type { Slot } from './useWidgetData'

/** chart-1..5 are the theme tokens; the 6th/7th slices are tints mixed from them. */
const SLICE_COLORS = [
    'var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)',
    'color-mix(in oklch, var(--chart-1) 50%, var(--background))',
]
const OTHER_COLOR = 'var(--muted-foreground)'

function NoData() {
    return (
        <Empty className="min-h-32 border-0 p-4">
            <EmptyHeader>
                <EmptyMedia variant="icon"><BarChart3 /></EmptyMedia>
                <EmptyTitle className="text-sm">No data for the selected filters</EmptyTitle>
                <EmptyDescription>Try widening the date range or filters.</EmptyDescription>
            </EmptyHeader>
        </Empty>
    )
}

/** Screen-reader table with the same numbers as the chart (the chart itself is role="img"). */
function DataTable({ caption, rows }: { caption: string; rows: { label: string; value: number }[] }) {
    return (
        <table className="sr-only">
            <caption>{caption}</caption>
            <thead><tr><th>Label</th><th>Value</th></tr></thead>
            <tbody>{rows.map((r, i) => <tr key={i}><td>{r.label}</td><td>{r.value}</td></tr>)}</tbody>
        </table>
    )
}

const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s)

function BarBody({ widget, result }: { widget: Widget; result: WidgetResult }) {
    const unit = result.unit || 'Count'
    const config: ChartConfig = { value: { label: unit, color: 'var(--chart-1)' } }
    const data = result.series.map((p) => ({ label: pointLabel(widget, p), value: p.value }))
    const longest = Math.max(...data.map((d) => Math.min(d.label.length, 28)))
    const yw = Math.min(190, Math.max(70, longest * 6.5))
    const height = Math.max(150, data.length * 34 + 16)
    return (
        <>
            <ChartContainer config={config} className="aspect-auto w-full" style={{ height }} role="img" aria-label={summarize(widget, result)}>
                <BarChart data={data} layout="vertical" margin={{ left: 0, right: 36 }} barCategoryGap={6}>
                    <CartesianGrid horizontal={false} />
                    <YAxis dataKey="label" type="category" width={yw} tickLine={false} axisLine={false} tickFormatter={(v: string) => truncate(v, 28)} interval={0} />
                    <XAxis type="number" hide />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel={false} />} />
                    <Bar dataKey="value" fill="var(--color-value)" radius={4} maxBarSize={32}>
                        <LabelList dataKey="value" position="right" className="fill-foreground" fontSize={12} formatter={(v) => formatValue(Number(v), result.unit)} />
                    </Bar>
                </BarChart>
            </ChartContainer>
            <DataTable caption={widget.title} rows={data} />
        </>
    )
}

function PieBody({ widget, result }: { widget: Widget; result: WidgetResult }) {
    const slices = pieRollup(result.series.map((p) => ({ ...p, label: pointLabel(widget, p) })), 6)
    if (slices.length === 0) return <NoData />
    const data = slices.map((s, i) => ({ ...s, fill: s.isOther ? OTHER_COLOR : SLICE_COLORS[i % SLICE_COLORS.length] }))
    const config: ChartConfig = Object.fromEntries(data.map((d) => [d.label, { label: d.label, color: d.fill }]))
    return (
        <div className="flex flex-col items-center gap-3 sm:flex-row">
            <ChartContainer config={config} className="aspect-square size-44 shrink-0" role="img" aria-label={summarize(widget, result)}>
                <PieChart>
                    <ChartTooltip content={<ChartTooltipContent nameKey="label" hideLabel />} />
                    <Pie data={data} dataKey="value" nameKey="label" innerRadius={44} outerRadius={80} strokeWidth={2} stroke="var(--card)" />
                </PieChart>
            </ChartContainer>
            <ul className="flex w-full min-w-0 flex-1 flex-col gap-1.5 text-sm" aria-label={`${widget.title} legend`}>
                {data.map((d) => (
                    <li key={d.key} className="flex items-center gap-2">
                        <span className="size-2.5 shrink-0 rounded-sm" style={{ background: d.fill }} aria-hidden />
                        <span className="min-w-0 flex-1 truncate" title={d.label}>{d.label}</span>
                        <span className="tabular-nums font-medium">{formatValue(d.value, result.unit)}</span>
                        <span className="w-12 text-right tabular-nums text-muted-foreground">{(d.percent * 100).toFixed(d.percent < 0.1 ? 1 : 0)}%</span>
                    </li>
                ))}
            </ul>
        </div>
    )
}

function LineBody({ widget, result }: { widget: Widget; result: WidgetResult }) {
    const unit = result.unit || 'Count'
    const config: ChartConfig = { value: { label: unit, color: 'var(--chart-1)' } }
    const data = result.series.map((p) => ({ label: pointLabel(widget, p), value: p.value }))
    return (
        <>
            <ChartContainer config={config} className="aspect-auto h-56 w-full" role="img" aria-label={summarize(widget, result)}>
                <AreaChart data={data} margin={{ left: 4, right: 12, top: 8 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
                    <YAxis width={unit === '%' ? 44 : 36} tickLine={false} axisLine={false} allowDecimals={false} tickFormatter={(v: number) => formatValue(v, result.unit)} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Area dataKey="value" type="monotone" stroke="var(--color-value)" strokeWidth={2} fill="var(--color-value)" fillOpacity={0.15} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </AreaChart>
            </ChartContainer>
            <DataTable caption={widget.title} rows={data} />
        </>
    )
}

function KpiBody({ widget, result }: { widget: Widget; result: WidgetResult }) {
    return (
        <div className="flex flex-col gap-1 py-2" role="img" aria-label={summarize(widget, result)}>
            <span className="text-4xl font-semibold tracking-tight tabular-nums">{formatValue(result.total, result.unit)}</span>
            {result.unit && result.unit !== '%' && <span className="text-sm text-muted-foreground">{result.unit}</span>}
        </div>
    )
}

function cell(row: ResultRow, c: NonNullable<WidgetResult['columns']>[number]): React.ReactNode {
    if (c.kind === 'core') {
        if (c.key === 'status') return <StatusBadge status={row.status as SubmissionStatus} />
        if (c.key === 'submitted_at') return row.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : '—'
        const v = row[c.key]
        return v === undefined || v === null || v === '' ? '—' : String(v)
    }
    const v = row.answers?.[c.key]
    if (v === undefined || v === null || v === '') return '—'
    return Array.isArray(v) ? v.join(', ') : String(v)
}

function TableBody_({ widget, result, onOpenRow }: { widget: Widget; result: WidgetResult; onOpenRow?: (id: string) => void }) {
    const rows = result.rows ?? []
    const cols = result.columns ?? []
    if (rows.length === 0) return <NoData />
    return (
        <div className="flex flex-col gap-2">
            <Table aria-label={widget.title}>
                <TableHeader><TableRow>{cols.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow
                            key={r.id}
                            className={cn(onOpenRow && 'cursor-pointer')}
                            tabIndex={onOpenRow ? 0 : undefined}
                            onClick={onOpenRow ? () => onOpenRow(r.id) : undefined}
                            onKeyDown={onOpenRow ? (e) => { if (e.key === 'Enter') onOpenRow(r.id) } : undefined}
                        >
                            {cols.map((c) => <TableCell key={c.key} className="max-w-56 truncate">{cell(r, c)}</TableCell>)}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {result.truncated && <p className="text-xs text-muted-foreground">Showing the first {rows.length} rows. Export CSV for the full set.</p>}
        </div>
    )
}

/** Renders one widget result (also used by the editor's live preview). */
export function WidgetBody({ widget, result, onOpenRow }: { widget: Widget; result: WidgetResult; onOpenRow?: (id: string) => void }) {
    if (result.error) return <ErrorState message={result.error} />
    switch (widget.type) {
        case 'kpi': return <KpiBody widget={widget} result={result} />
        case 'table': return <TableBody_ widget={widget} result={result} onOpenRow={onOpenRow} />
        default:
            if (result.series.length === 0 || result.series.every((p) => p.value === 0 && widget.type !== 'bar')) return <NoData />
            if (widget.type === 'bar') return <BarBody widget={widget} result={result} />
            if (widget.type === 'pie') return <PieBody widget={widget} result={result} />
            return <LineBody widget={widget} result={result} />
    }
}

export function ErrorState({ message, problems }: { message: string; problems?: string[] }) {
    return (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            <div className="flex flex-col gap-1">
                <span>{message}</span>
                {problems && problems.length > 0 && <ul className="list-disc pl-4">{problems.map((p, i) => <li key={i}>{p}</li>)}</ul>}
            </div>
        </div>
    )
}

function ago(at: number | undefined, now: number): string {
    if (!at) return ''
    const s = Math.max(0, Math.round((now - at) / 1000))
    if (s < 45) return 'Updated just now'
    const m = Math.round(s / 60)
    if (m < 60) return `Updated ${m} min ago`
    return `Updated ${Math.round(m / 60)} h ago`
}

interface TileProps {
    widget: Widget
    slot?: Slot
    editing?: boolean
    canMoveUp?: boolean
    canMoveDown?: boolean
    onMove?: (delta: number) => void
    onRefresh: () => void
    onEdit?: () => void
    onDuplicate?: () => void
    onRemove?: () => void
    onExport?: () => void
    onOpenRow?: (id: string) => void
}

export function WidgetTile({ widget, slot, editing, canMoveUp, canMoveDown, onMove, onRefresh, onEdit, onDuplicate, onRemove, onExport, onOpenRow }: TileProps) {
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30_000)
        return () => clearInterval(t)
    }, [])
    const loading = slot?.loading ?? true
    const hasMenu = editing || onExport

    return (
        <Card className="h-full" aria-busy={loading}>
            <CardHeader>
                <CardTitle className="truncate text-sm font-medium" title={widget.title}>{widget.title}</CardTitle>
                <CardAction className="flex items-center gap-1">
                    {editing && (
                        <>
                            <Button variant="ghost" size="icon-sm" aria-label={`Move ${widget.title} earlier`} disabled={!canMoveUp} onClick={() => onMove?.(-1)}><ArrowUp /></Button>
                            <Button variant="ghost" size="icon-sm" aria-label={`Move ${widget.title} later`} disabled={!canMoveDown} onClick={() => onMove?.(1)}><ArrowDown /></Button>
                        </>
                    )}
                    {hasMenu && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${widget.title}`}><MoreVertical /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuGroup>
                                    {editing && <DropdownMenuItem onSelect={onEdit}><Pencil /> Edit</DropdownMenuItem>}
                                    {editing && <DropdownMenuItem onSelect={onDuplicate}><Copy /> Duplicate</DropdownMenuItem>}
                                    {onExport && <DropdownMenuItem onSelect={onExport}><Download /> Export CSV</DropdownMenuItem>}
                                </DropdownMenuGroup>
                                {editing && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuGroup>
                                            <DropdownMenuItem variant="destructive" onSelect={onRemove}><Trash2 /> Remove</DropdownMenuItem>
                                        </DropdownMenuGroup>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </CardAction>
            </CardHeader>
            <CardContent className="min-w-0 flex-1">
                {slot?.result ? (
                    <div className={cn(loading && 'opacity-60 transition-opacity')}>
                        <WidgetBody widget={widget} result={slot.result} onOpenRow={onOpenRow} />
                    </div>
                ) : slot?.error ? (
                    <ErrorState message={slot.error} problems={slot.problems} />
                ) : (
                    <Skeleton className="h-32 w-full" />
                )}
            </CardContent>
            <CardFooter className="justify-between gap-2 text-xs text-muted-foreground">
                <span aria-live="polite">{loading ? 'Updating…' : ago(slot?.at, now)}</span>
                <Button variant="ghost" size="icon-sm" aria-label={`Refresh ${widget.title}`} onClick={onRefresh} disabled={loading}><RefreshCw /></Button>
            </CardFooter>
        </Card>
    )
}
